// Report KPI card: label, headline value, and an optional change-vs-previous
// chip. Used by the reports overview and the sales-overview detail page.
import { Sparkline } from './charts';

const MUTED = '#8e8e93';

export function DeltaChip({ delta, neutral }: { delta: number | null; neutral?: boolean }) {
  if (delta === null) return null;
  const rounded = Math.round(delta * 10) / 10;
  const flat = rounded === 0;
  const up = rounded > 0;
  // `neutral` metrics (discounts, refunds) have no "good" direction.
  const color = neutral || flat ? MUTED : up ? '#248a3d' : '#c9372c';
  const bg =
    neutral || flat
      ? 'rgba(142,142,147,0.12)'
      : up
        ? 'rgba(36,138,61,0.12)'
        : 'rgba(201,55,44,0.12)';
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 2,
        padding: '2px 7px',
        borderRadius: 999,
        background: bg,
        color,
        fontSize: 12,
        fontWeight: 600,
        lineHeight: 1.4,
      }}
    >
      {flat ? '±' : up ? '▲' : '▼'}
      {Math.abs(rounded).toFixed(1)}%
    </span>
  );
}

export function MetricCard({
  label,
  value,
  delta = null,
  previous,
  caption,
  neutral,
  trend,
  className,
}: {
  label: string;
  value: string;
  delta?: number | null;
  /** Per-day series for this metric, drawn as a sparkline under the figures. */
  trend?: number[];
  /** Already-formatted baseline value, shown as "vs <previous>" under the
   *  number. Every comparable metric carries one, so the % is never a figure
   *  the reader has to take on trust. */
  previous?: string;
  caption?: string;
  neutral?: boolean;
  /** Grid placement from the parent row; the card owns its own look. */
  className?: string;
}) {
  return (
    <div
      className={className}
      style={{
        minWidth: 0,
        padding: 14,
        background: 'var(--f7-card-bg-color,#fff)',
        borderRadius: 14,
        boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
      }}
    >
      <div
        style={{
          color: MUTED,
          fontSize: 12,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
        }}
      >
        {label}
      </div>
      {/* Deliberately uncoloured: on this page colour means direction (the
          delta chip is green up / red down), so a themed value would read as a
          loss - the brand accent is a red-orange. */}
      <div style={{ fontSize: 22, fontWeight: 700, marginTop: 4 }}>{value}</div>
      {(delta !== null || previous || caption) && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 6,
            marginTop: 8,
          }}
        >
          <DeltaChip delta={delta} neutral={neutral} />
          {previous && <span style={{ color: MUTED, fontSize: 12 }}>{previous}</span>}
          {caption && <span style={{ color: MUTED, fontSize: 12 }}>{caption}</span>}
        </div>
      )}
      {trend && <Sparkline values={trend} />}
    </div>
  );
}
